import {
  type CardWatchSuggestion,
  type ChildProgress,
} from '@littlebird/action-cards';
import {
  isRunRefused,
  type CardActionHandlers,
  type CardDoneVia,
  type CardState,
} from '@littlebird/action-cards-view';
import {
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { type CardOpen } from '../cardOpen';
import {
  HostHiddenActionsContext,
  HostIconActionsContext,
  HostTrailingActionsContext,
  ShortcutRunKeyContext,
} from '../registry';
import { type RunEnd, type RunEvents } from '../runEvents';

import { FeedCompactRow } from './FeedCompactRow';
import { FeedContextLine } from './FeedContextLine';
import { FeedDensityContext, isDenseFeed } from './feedDensity';
import { FeedFrameHeader } from './FeedFrameHeader';
import { useFeedKeyScope } from './FeedKeyScope';
import { FeedKeysAnsweredContext, FeedKeyTooltip } from './FeedKeyTooltip';
import { capitalized } from './feedKind';
import { FeedMarkDone } from './FeedMarkDone';
import { FeedMeetingLink } from './FeedMeetingLink';
import { FeedMorph } from './FeedMorph';
import { FeedOpenLink } from './FeedOpenLink';
import { FeedRow, type FeedRowProps } from './FeedRow';
import {
  feedRowView,
  isActionableStatus,
  offersMarkDone,
  type FeedPrimaryPress,
} from './feedRowView';
import { focusFirstField, handleFeedShortcut } from './feedShortcuts';
import {
  FeedSourceChip,
  FeedSourceMenuItem,
  hasFeedSourceLine,
} from './FeedSourceChip';
import { FeedWatchChip, FeedWatchSuggestion } from './FeedWatchSuggestion';
import { useFeedFocusHandoff } from './useFeedFocusHandoff';

import type { CardHeaderRenderer } from '../CardV2';

export type FeedCardWatch = {
  suggestion: CardWatchSuggestion | null;
  // Accepting a to-do's suggestion ticks it off through the row's own press
  accept: (acknowledge: () => void) => void;
  keep: () => void;
};

export type FeedCardViewProps = {
  state: CardState;
  // Read when a run resolves, since the card may have failed after this render
  currentStatus: () => CardState['status'];
  press: Pick<CardActionHandlers, 'run' | 'acknowledge' | 'chat'>;
  // Marks a draft done without running it; the row's checkbox offers it
  completeElsewhere?: (via: CardDoneVia) => Promise<void>;
  icon: ReactNode;
  todo?: boolean;
  appName: string;
  open: boolean;
  keepOpen?: boolean;
  onToggle: () => void;
  onDismiss: () => void;
  onChat: () => void;
  // P and S, which press the row's Pin and Snooze
  onPin?: () => void;
  onSnooze?: () => void;
  onOpenUrl: CardOpen;
  onOpenSource?: () => void;
  // The meeting that made the card, which `onOpenSource` opens
  meetingSource?: { name: string; startedAt: Date };
  // Called when a run is pressed, before it starts
  onRetain?: () => void;
  // Called once a run press ends, however it ended
  onRunEnd?: (end: RunEnd) => void;
  // The host slides a done card out, so an open frame stays open until it leaves
  leavesWhenDone?: boolean;
  // Shown while the card can still be acted on, before the frame's ⋯ and Close
  actions?: ReactNode;
  // The row's Pin, Snooze and its ⋯ menu with Assign, shown on hover while the card can still be acted on
  rowActions?: ReactNode;
  snoozedUntil?: Date;
  // The ⋯ menu beside the frame's Close
  // The open card's ⋯ menu, given the source line when a dense card moves it there
  more?: (source: ReactNode) => ReactNode;
  // What the card's watcher says, shown while the card can still be acted on
  watch?: FeedCardWatch;
  // The open card answers its keys wherever focus is in the host's `FeedKeyScope`, beyond its own frame
  keyboardTarget?: boolean;
  // The cards under this one, counted on the row
  subtasks?: ChildProgress;
  // The card's latest activity, such as a chat reply or a new subtask; the age reads it over the served age
  lastActiveAt?: string;
  // A note only staff see, such as the backend that made the card, on the row and the frame's header
  staffLabel?: ReactNode;
  // The host draws its own body, such as a question's options, which has no action band for the source
  customBody?: boolean;
  // The open card leaves out its Chat button
  chatless?: boolean;
  frame: (header: CardHeaderRenderer, run: RunEvents) => ReactNode;
};

const CHATLESS_ACTIONS = ['-card_chat'];
const NO_HIDDEN_ACTIONS: readonly string[] = [];

// A card in Home and the Feed: its row at rest, opening in place into the frame
export function FeedCardView({
  state,
  currentStatus,
  press: handlers,
  completeElsewhere,
  icon,
  todo = false,
  appName,
  open,
  keepOpen = false,
  onToggle,
  onDismiss,
  onChat,
  onPin,
  onSnooze,
  onOpenUrl,
  onOpenSource,
  meetingSource,
  onRetain = () => {},
  onRunEnd,
  leavesWhenDone = false,
  actions,
  rowActions,
  snoozedUntil,
  more,
  watch,
  keyboardTarget = false,
  subtasks,
  lastActiveAt,
  staffLabel,
  customBody = false,
  chatless = false,
  frame,
}: FeedCardViewProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  useFeedFocusHandoff(open, true, { frameRef, rowRef });
  const density = useContext(FeedDensityContext);
  const compact = isDenseFeed(density);
  const [runPending, setRunPending] = useState(false);
  const view = feedRowView(state, { runPending, lastActiveAt });
  const actionable = isActionableStatus(state.status);
  const primary = state.primary_action;
  const { pill } = view;
  const live = actionable && !view.running;
  const framed = open || keepOpen;

  // An open frame folds back to the row, which reads the done text. A settled run can have failed, and a failed run
  // stays open on Retry
  const foldWhenSettled = () => {
    if (frameRef.current && !leavesWhenDone && currentStatus() !== 'failed')
      onToggle();
  };

  const press = async (primaryPress: FeedPrimaryPress) => {
    if (primaryPress.kind === 'chat') {
      handlers.chat({ prompt: primaryPress.prompt, attach: true });
      return;
    }
    if (primaryPress.kind === 'continue') {
      handlers.chat({ prompt: '', mode: 'card' });
      return;
    }
    onRetain();
    setRunPending(true);
    let end: RunEnd = 'threw';
    try {
      const outcome =
        primaryPress.kind === 'acknowledge'
          ? await handlers.acknowledge(primaryPress.via)
          : await handlers.run(primaryPress.key);
      const refused = isRunRefused(outcome);
      end = refused ? 'refused' : 'settled';
      // A refusal is about the card's fields, which only the open card shows
      if (refused && !open) onToggle();
      else if (!refused) foldWhenSettled();
    } catch {
      // The run command has already told the user with a toast
    } finally {
      setRunPending(false);
      onRunEnd?.(end);
    }
  };

  const markDone =
    offersMarkDone(todo, state.status) && !view.running
      ? (via: CardDoneVia) => void press({ kind: 'acknowledge', via })
      : undefined;

  // A draft the user already handled elsewhere is marked done without running it
  const completeDraft =
    completeElsewhere && pill?.press.kind === 'run' && state.status === 'draft'
      ? async (via: CardDoneVia) => {
          onRetain();
          let end: RunEnd = 'threw';
          try {
            await completeElsewhere(via);
            end = 'settled';
          } finally {
            onRunEnd?.(end);
          }
        }
      : undefined;

  // Each card has one way to be checked off: a to-do's box, a draft's box, or an update's app mark. The collapsed
  // row and the open card's header draw it on the card's icon, and D presses it
  const isUpdate = state.fields.kind === 'info';
  // Settles once the press does, so a check that did not go through can be tried again
  const checkOff: ((via: CardDoneVia) => Promise<void>) | undefined = !live
    ? undefined
    : isUpdate
      ? state.status === 'draft'
        ? (via) => press({ kind: 'acknowledge', via })
        : undefined
      : offersMarkDone(todo, state.status)
        ? (via) => press({ kind: 'acknowledge', via })
        : completeDraft;
  const rowCheck: Pick<
    FeedRowProps,
    'onMarkDone' | 'checkKind' | 'checkLabel'
  > = checkOff
    ? {
        onMarkDone: () => checkOff('row_checkbox'),
        checkKind: isUpdate ? 'mark' : 'box',
        ...(isUpdate ? { checkLabel: 'Mark as read' } : {}),
      }
    : {};

  const primaryShortcut =
    primary && actionable && !view.running
      ? () => void press({ kind: 'run', key: primary.key })
      : pill && !view.running
        ? () =>
            void press(
              pill.press.kind === 'acknowledge'
                ? { kind: 'acknowledge', via: 'shortcut' }
                : pill.press
            )
        : markDone && (() => markDone('shortcut'));
  const clickPrimaryButton = () =>
    frameRef.current
      ?.querySelector<HTMLButtonElement>('[data-card-primary="true"]')
      ?.click();

  // The snooze times open from the row's Snooze, so S on an open card closes it first. An update's row has no hover
  // buttons to open them from, and a parent kept open for its subtask has no row of its own
  const snoozeFromRow =
    onSnooze && live && !isUpdate && (open || !framed)
      ? () => {
          if (open) onToggle();
          onSnooze();
        }
      : undefined;

  const shortcuts = {
    toggle: onToggle,
    edit: open ? () => focusFirstField(frameRef.current) : undefined,
    chat: onChat,
    dismiss: actionable ? onDismiss : undefined,
    runPrimary: framed ? clickPrimaryButton : primaryShortcut,
    markDone: checkOff && (() => void checkOff('shortcut')),
    pin: live ? onPin : undefined,
    snooze: snoozeFromRow,
  };

  const suggestion =
    watch?.suggestion && actionable && !view.running ? watch.suggestion : null;
  const watchStrip =
    watch && suggestion ? (
      <FeedWatchSuggestion
        suggestion={suggestion}
        primary={Boolean(markDone) && !pill && suggestion.kind === 'complete'}
        onAccept={() =>
          watch.accept(
            () => void press({ kind: 'acknowledge', via: 'card_button' })
          )
        }
        onKeep={watch.keep}
      />
    ) : null;
  const watchChip = suggestion ? (
    <FeedWatchChip kind={suggestion.kind} />
  ) : undefined;

  const done = view.outcome?.tone === 'done';
  const title = todo ? capitalized(view.title) : view.title;
  const description = state.reason ?? state.preview ?? undefined;

  const rowProps = {
    view,
    icon,
    todo,
    struck: todo && done,
    appName,
    onOpen: onToggle,
    onDismiss: live ? onDismiss : undefined,
    // The row shows only its title; its section already says what kind of card it is. Chat stays on C and the open card
    hoverActions: live ? rowActions : undefined,
    staffLabel,
    ...rowCheck,
    hoverless: isUpdate,
    backAt: snoozedUntil,
    subtasks,
    onPress: (primaryPress) => void press(primaryPress),
    onOpenUrl,
    onKeyDown: (event) => handleFeedShortcut(event, shortcuts),
  } satisfies Omit<FeedRowProps, 'description'>;

  useLayoutEffect(() => {
    const frameElement = frameRef.current;
    if (!framed || !frameElement) return;

    const observed = new WeakSet<Element>();
    const resizes = new ResizeObserver(() => alignLabels());

    const alignLabels = () => {
      for (const object of frameElement.querySelectorAll<HTMLElement>(
        '[data-card-object]'
      )) {
        const labels = [
          ...object.querySelectorAll<HTMLElement>(
            '[data-field-label] > span, .card-field-labelled > label'
          ),
        ].filter((label) => getComputedStyle(label).position !== 'absolute');
        for (const label of labels) {
          if (observed.has(label)) continue;
          observed.add(label);
          resizes.observe(label);
        }

        const widths = labels.map((label) => {
          const range = document.createRange();
          range.selectNodeContents(label);
          return range.getBoundingClientRect().width;
        });
        if (widths.length < 2) object.style.removeProperty('--feed-label-w');
        else
          object.style.setProperty(
            '--feed-label-w',
            `${Math.ceil(Math.max(...widths))}px`
          );
      }
    };

    resizes.observe(frameElement);
    alignLabels();
    return () => resizes.disconnect();
  }, [framed]);

  const answersScope = useFeedKeyScope(open && keyboardTarget, (event) =>
    handleFeedShortcut(event, shortcuts, frameRef.current)
  );

  // The open card's band holds its own buttons, then the app it opens in as that app's mark and name at the far end.
  // The line of where the card came from waits in ⋯, in every density
  const sourceInMenu = view.openUrl
    ? hasFeedSourceLine(state.origin)
    : hasFeedSourceLine(state.origin, view.age);
  const meetingLink =
    meetingSource && onOpenSource ? (
      <FeedMeetingLink meeting={meetingSource} onOpenSource={onOpenSource} />
    ) : null;
  const appLink = view.openUrl ? (
    <FeedSourceChip
      icon={icon}
      appName={appName}
      origin={state.origin}
      age={view.age}
      openUrl={view.openUrl}
      onOpenUrl={onOpenUrl}
      onOpenSource={onOpenSource}
    />
  ) : null;
  const bandActions = (
    <>
      {/* The watcher's "Looks done" strip already offers Mark done */}
      {markDone && suggestion?.kind !== 'complete' ? (
        <FeedMarkDone
          title={view.title}
          primary={!pill}
          onClick={() => markDone('card_button')}
        />
      ) : null}
      {meetingLink ? (
        // The band's far end holds one source; the meeting sits before the app, whose mark keeps the very end
        <span data-feed-source="" className="flex min-w-0 items-center">
          {meetingLink}
          {appLink}
        </span>
      ) : (
        appLink
      )}
    </>
  );

  return (
    <FeedMorph open={framed}>
      {framed ? (
        <div
          ref={frameRef}
          tabIndex={-1}
          className="outline-none"
          data-feed-frame=""
          data-feed-todo={todo || undefined}
          onKeyDown={
            answersScope
              ? undefined
              : (event) => {
                  // An open subtask's card already answered this key through the scope
                  if (event.defaultPrevented) return;
                  handleFeedShortcut(event, shortcuts, frameRef.current);
                }
          }
        >
          <FeedKeysAnsweredContext.Provider value={true}>
            <ShortcutRunKeyContext.Provider
              value={
                primary?.key ??
                (pill?.press.kind === 'acknowledge' ? 'acknowledge' : undefined)
              }
            >
              <HostIconActionsContext.Provider value={FeedKeyTooltip}>
                <HostHiddenActionsContext.Provider
                  value={chatless ? CHATLESS_ACTIONS : NO_HIDDEN_ACTIONS}
                >
                  <HostTrailingActionsContext.Provider value={bandActions}>
                    <FeedContextLine enabled={compact}>
                      {frame(
                        (bodyTitle, bodyActions, bodyShowsReason) => (
                          <>
                            <FeedFrameHeader
                              view={view}
                              title={bodyTitle ?? title}
                              actions={
                                <>
                                  {bodyActions}
                                  {/* A host's own body has no action band for "Open in", so its links sit here */}
                                  {customBody ? meetingLink : null}
                                  {customBody && view.openUrl ? (
                                    <FeedOpenLink
                                      variant="arrow"
                                      url={view.openUrl}
                                      appName={appName}
                                      onOpenUrl={onOpenUrl}
                                    />
                                  ) : null}
                                  {live ? actions : null}
                                </>
                              }
                              staffLabel={staffLabel}
                              struck={todo && done}
                              subtasks={subtasks}
                              // A dense card's context line takes the reason from the document's Reason; a host's own
                              // body has none, so its reason stays under the title unless the body shows it
                              description={
                                (compact && !customBody) || bodyShowsReason
                                  ? undefined
                                  : (state.reason ?? undefined)
                              }
                              icon={icon}
                              onCheck={rowCheck.onMarkDone}
                              checkKind={rowCheck.checkKind}
                              checkLabel={rowCheck.checkLabel}
                              onClose={onToggle}
                              collapses={compact}
                              more={more?.(
                                sourceInMenu ? (
                                  <FeedSourceMenuItem
                                    icon={icon}
                                    origin={state.origin}
                                    age={view.age}
                                    onOpenSource={onOpenSource}
                                  />
                                ) : null
                              )}
                            />
                            {watchStrip}
                          </>
                        ),
                        {
                          onRun: onRetain,
                          onRunSettled: foldWhenSettled,
                          onRunEnd,
                        }
                      )}
                    </FeedContextLine>
                  </HostTrailingActionsContext.Provider>
                </HostHiddenActionsContext.Provider>
              </HostIconActionsContext.Provider>
            </ShortcutRunKeyContext.Provider>
          </FeedKeysAnsweredContext.Provider>
        </div>
      ) : (
        <div ref={rowRef}>
          {compact ? (
            <FeedCompactRow {...rowProps} watchSuggestion={watchChip} />
          ) : (
            <FeedRow
              {...rowProps}
              description={description}
              watchSuggestion={watchChip}
            />
          )}
        </div>
      )}
    </FeedMorph>
  );
}
